import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';

// mui (KareHero design system — themed via llama ThemeProvider)
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

// llama design-system icon
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import { inFrameDialogProps } from '@/designs-dev/device/inFrameDialog';
import ShortlistSelect from '@/components/mui/ShortlistSelect';
import ShortlistModal from '@/components/mui/ShortlistModal';
import {
  ALL_FILTER_IDS,
  CARE_TYPES,
  DISMISS_REASONS,
  FILTERS_BY_CARE_TYPE,
  INITIAL_CARE_TYPE,
  INITIAL_FILTERS,
  SECURED_ELSEWHERE,
  SORT_OPTIONS_BY_CARE_TYPE,
  STATUSES,
  STATUS_COLOR,
  type CareTypeId,
  type SortOption,
  type Status,
} from '@/cam-admin/care-search/CareSearch.content';
import {
  CURRENT_CAM,
  HOME_PIN,
  MAP_PINS,
  PROVIDER_DETAIL,
  RESULTS,
  SECURED_ELSEWHERE_DECLARATION,
  SEED_ITEMS,
  SEED_NOTES,
  type Provider,
  type ProviderNote,
  type SecuredElsewhere,
  type Shortlist,
  type ShortlistItem,
} from '@/cam-admin/care-search/CareSearch.fixtures';
import { CAM_COMPACT } from '@/cam-admin/responsive';
import { ShellOverlay } from '@/cam-admin/control-panel/bits';
import StatusChip from '@/cam-admin/paywall/StatusChip';
// Lives in its own module so the shortlists tab can render the same menu
// without importing this file, which already imports the tab's StatusChip.
import StatusChipMenu from '@/cam-admin/care-search/StatusChipMenu';
import SearchField from '@/components/mui/SearchField';
export { StatusChipMenu };
import {
  providersFor,
  type PaywallState,
  type Scenario,
} from '@/cam-admin/paywall/Shortlist.fixtures';

// ─── mock data ───────────────────────────────────────────────────────────────

const today = () =>
  new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const toItem = (p: Provider): ShortlistItem => ({
  name: p.name,
  address: p.address,
  phone: p.phone,
});

const milesOf = (p: Provider) => parseFloat(p.distance) || 0;
const priceOf = (p: Provider) => parseInt(p.price.replace(/\D/g, ''), 10) || 0;
const ratingOf = (p: Provider) =>
  p.tags.includes('Rating Outstanding')
    ? 2
    : p.tags.includes('Rating Good')
      ? 1
      : 0;

const SORTERS: Record<SortOption, (a: Provider, b: Provider) => number> = {
  'Most relevant': () => 0,
  Nearest: (a, b) => milesOf(a) - milesOf(b),
  'Highest rating': (a, b) => ratingOf(b) - ratingOf(a),
  'Lowest price': (a, b) => priceOf(a) - priceOf(b),
  'Highest price': (a, b) => priceOf(b) - priceOf(a),
};

// ─── prototype scenarios (driven from the sidebar) ──────────────────────────────

export type ScenarioId =
  'firstVisit' | 'searchedNoList' | 'draft' | 'published' | 'securedElsewhere';

const scenarioState = (
  id: ScenarioId,
): {
  searched: boolean;
  shortlists: Shortlist[];
  // Set when the care circle has told us they arranged care outside these
  // shortlists — the shortlists stay as they were, the search is just over.
  securedElsewhere?: SecuredElsewhere;
} => {
  switch (id) {
    case 'firstVisit':
      return { searched: false, shortlists: [] };
    case 'searchedNoList':
      return { searched: true, shortlists: [] };
    case 'draft':
      return {
        searched: true,
        shortlists: [
          {
            id: 's1',
            name: 'Shortlist 1 - 1/06/2026',
            status: 'draft',
            items: SEED_ITEMS,
          },
        ],
      };
    case 'published':
      return {
        searched: true,
        shortlists: [
          {
            id: 's1',
            name: 'Shortlist 1 - 1/06/2026',
            status: 'published',
            items: SEED_ITEMS,
          },
        ],
      };
    case 'securedElsewhere':
      return {
        searched: true,
        shortlists: [
          {
            id: 's1',
            name: 'Shortlist 1 - 1/06/2026',
            status: 'published',
            items: SEED_ITEMS,
          },
        ],
        securedElsewhere: SECURED_ELSEWHERE_DECLARATION,
      };
  }
};

// Initial UI state for handover minis: jump straight to a scenario, an open
// dialog or the provider detail without clicking through.
export type CareSearchInitial = {
  scenario?: ScenarioId;
  // Open the detail overlay for one of the search results, by name.
  detailProvider?: string;
  modal?: 'create' | 'edit';
  publish?: 'one';
  // Search pane view mode.
  view?: 'list' | 'map';
  // Start with the dismissed section of the current shortlist expanded.
  showDismissed?: boolean;
  // Open the dismiss dialog for a shortlist item, by name.
  dismiss?: string;
  // Open the global notes dialog for one of the search results, by name.
  notes?: string;
};

// ─── shared bits ─────────────────────────────────────────────────────────────

// Status-style badges sit a notch smaller than MUI's small chip, so they
// read as metadata rather than competing with buttons and controls.
const badgeSx = { height: 20 } as const;

// "Rating Good" → "Rating <b>Good</b>" to match the Figma chip.
const renderTag = (t: string) => {
  const m = /^Rating (.+)$/.exec(t);
  return m ? (
    <span>
      Rating <strong>{m[1]}</strong>
    </span>
  ) : (
    t
  );
};

// The CQC rating grade (Outstanding / Good / Adequate), pulled off the tags.
const careRatingGrade = (p: Provider): string | null => {
  const tag = p.tags.find((t) => t.startsWith('Rating '));
  if (!tag) return null;
  return tag.replace('Rating ', '');
};
// The care type is the first non-rating tag (Residential / Nursing).
const careTypeOf = (p: Provider): string =>
  p.tags.find((t) => !t.startsWith('Rating ')) ?? p.tags[0];
// Availability reads green when there are spaces, amber otherwise — so a list
// scans at a glance.
// Availability maps to the system status palette: green available, amber
// waitlist, red unavailable, blue contacted.
const availabilityColor = (
  status: string,
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'Available':
      return 'success';
    case 'Waitlist':
      return 'warning';
    case 'Unavailable':
      return 'error';
    case 'Contacted':
      return 'info';
    default:
      return 'default';
  }
};

// Stops a click on an interactive control (button/chip/menu) from also
// triggering a clickable card's onClick.
const stop = (e: React.MouseEvent) => e.stopPropagation();

/**
 * Shared provider content used for both search results and shortlist items, so
 * the typography (h4 name, body1 address/phone) stays consistent. It's content
 * only: call sites place it inside a Card (or any other container), keeping
 * one card primitive. Variants differ via `inlineChip`, `right`, `children`.
 */
export const ProviderCardContent = ({
  name,
  address,
  distance,
  phone,
  photo,
  inlineChip,
  spaceBetweenHeader,
  right,
  children,
}: {
  name: string;
  address: string;
  distance?: string;
  phone?: string;
  // Cover photo placeholder block (search results).
  photo?: boolean;
  inlineChip?: React.ReactNode;
  spaceBetweenHeader?: boolean;
  right?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <>
    {/* outer Label ↔ Action gap = 16px (spacing 2) */}
    <Stack
      sx={{ alignItems: 'stretch', justifyContent: 'space-between' }}
      direction='row'
      spacing={2}
    >
      {photo && (
        <CardMedia
          sx={{
            width: 96,
            height: 96,
            borderRadius: '8px',
            bgcolor: 'custom.neutral.200',
            flexShrink: 0,
          }}
        />
      )}
      {/* left column: uniform 12px (spacing 1.5) between every row */}
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1.5}>
        <Stack
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: spaceBetweenHeader ? 'space-between' : 'flex-start',
          }}
          direction='row'
          spacing={2}
          useFlexGap
        >
          <Typography variant='h4'>{name}</Typography>
          {inlineChip && (
            <Box
              sx={{ display: 'inline-flex' }}
              component='span'
              onClick={stop}
            >
              {inlineChip}
            </Box>
          )}
        </Stack>
        <Typography variant='body1' color='text.secondary'>
          {address}
        </Typography>
        {distance && (
          <Typography variant='body1' color='text.secondary'>
            {distance}
          </Typography>
        )}
        {phone && (
          <Link
            href={`tel:${phone.replace(/\s/g, '')}`}
            variant='body1'
            onClick={stop}
          >
            {phone}
          </Link>
        )}
        {children && <Box onClick={stop}>{children}</Box>}
      </Stack>
      {right && (
        <Stack
          sx={{
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
          spacing={1.5}
          onClick={stop}
        >
          {right}
        </Stack>
      )}
    </Stack>
  </>
);

// "Add to shortlist" menu button (provider detail and result card). One
// button with a chevron that always opens the menu, so the user must explicitly
// pick a shortlist (or create one) — no implicit add to the current list.
const AddToShortlistMenuButton = ({
  provider,
  shortlists,
  selectedId,
  onAdd,
  onCreateNew,
  icon = false,
}: {
  provider: Provider;
  shortlists: Shortlist[];
  selectedId: string | null;
  onAdd: (provider: Provider, shortlistId: string) => void;
  onCreateNew: (provider: Provider) => void;
  // Compact plus trigger for the result card; defaults to the labelled button.
  icon?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      {icon ? (
        <IconButton
          aria-label='Add to shortlist'
          color='primary'
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <Icon icon={'plus' as never} fontSize={16} />
        </IconButton>
      ) : (
        <Button
          variant='outlined'
          size='small'
          color='primary'
          endIcon={<Icon icon='chevron-down' fontSize={12} />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          Add to shortlist
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {shortlists.map((s) => (
          <MenuItem
            key={s.id}
            selected={s.id === selectedId}
            onClick={() => {
              setAnchorEl(null);
              onAdd(provider, s.id);
            }}
          >
            {s.name}
          </MenuItem>
        ))}
        {shortlists.length > 0 && <Divider />}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onCreateNew(provider);
          }}
        >
          Create new shortlist
        </MenuItem>
      </Menu>
    </>
  );
};

// ─── map view (mock) ────────────────────────────────────────────────────────────

// An abstract street grid with hardcoded pins — a stand-in for a real map so
// the layout and interaction can be tested without a tiles dependency.
const MapView = ({
  results,
  onOpenDetail,
}: {
  results: Provider[];
  onOpenDetail: (provider: Provider) => void;
}) => {
  const [selected, setSelected] = useState<Provider | null>(null);

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          position: 'relative',
          height: 560,
          borderRadius: `${radius.md}px`,
          overflow: 'hidden',
          border: 1,
          borderColor: 'custom.neutral.200',
          bgcolor: 'custom.neutral.100',
        }}
      >
        {/* abstract streets */}
        {[16, 46, 74].map((y) => (
          <Box
            sx={{
              position: 'absolute',
              top: `${y}%`,
              left: 0,
              right: 0,
              height: 10,
              bgcolor: 'background.paper',
            }}
            key={`h${y}`}
          />
        ))}
        {[20, 50, 78].map((x) => (
          <Box
            sx={{
              position: 'absolute',
              left: `${x}%`,
              top: 0,
              bottom: 0,
              width: 10,
              bgcolor: 'background.paper',
            }}
            key={`v${x}`}
          />
        ))}

        {/* Margarette's home */}
        <Stack
          spacing={0.5}
          sx={{
            position: 'absolute',
            left: `${HOME_PIN.x}%`,
            top: `${HOME_PIN.y}%`,
            alignItems: 'center',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'cherry.main',
              color: 'common.white',
            }}
          >
            <Icon icon={'house' as never} fontSize={16} />
          </Box>
          <Chip size='small' label='Margarette’s home' />
        </Stack>

        {/* provider pins */}
        {results.map((p) => {
          const pin = MAP_PINS[p.name];
          if (!pin) return null;
          const isSelected = selected?.name === p.name;
          return (
            <Stack
              key={p.name}
              spacing={0.5}
              onClick={() => setSelected(p)}
              sx={{
                position: 'absolute',
                left: `${pin.x}%`,
                top: `${pin.y}%`,
                alignItems: 'center',
                transform: 'translate(-50%, -50%)',
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 1,
                  borderColor: 'custom.neutral.300',
                  bgcolor: isSelected ? 'primary.main' : 'background.paper',
                  color: isSelected ? 'common.white' : 'primary.main',
                }}
              >
                <Icon icon={'location-dot' as never} fontSize={16} />
              </Box>
              <Chip size='small' label={`${p.name} · ${p.distance}`} />
            </Stack>
          );
        })}

        {/* selected provider summary */}
        {selected && (
          <Box sx={{ position: 'absolute', left: 16, right: 16, bottom: 16 }}>
            <Card>
              <CardActionArea onClick={() => onOpenDetail(selected)}>
                <Box sx={{ p: 2 }}>
                  <ProviderCardContent
                    name={selected.name}
                    address={selected.address}
                    distance={selected.distance}
                    inlineChip={
                      <Chip
                        label={selected.status}
                        size='small'
                        color={availabilityColor(selected.status)}
                      />
                    }
                    right={
                      <Link
                        href={`tel:${selected.phone.replace(/\s/g, '')}`}
                        variant='body1'
                        onClick={stop}
                        noWrap
                      >
                        {selected.phone}
                      </Link>
                    }
                  />
                </Box>
              </CardActionArea>
            </Card>
          </Box>
        )}
      </Box>
      <Typography variant='caption' color='text.secondary'>
        Pin positions are illustrative. Distances are measured from Margarette’s
        home.
      </Typography>
    </Stack>
  );
};

// ─── left: search pane ─────────────────────────────────────────────────────────

// Vertical search result: MUI media card (photo on top, details under,
// actions along the bottom). A drill-mode boundary — hover inside it to
// inspect the pieces.
// ─── right: shortlist pane ─────────────────────────────────────────────────────

const ShortlistCard = ({
  item,
  onEditingChange,
  onOpenDetail,
  onDismiss,
}: {
  item: ShortlistItem;
  onEditingChange: (name: string, editing: boolean) => void;
  onOpenDetail: (provider: Provider) => void;
  onDismiss: (name: string) => void;
}) => {
  const [editing, setEditing] = useState(Boolean(item.editingInitial));
  const [noteOpen, setNoteOpen] = useState(false);
  const [status, setStatus] = useState<string>(item.status ?? 'Not contacted');

  // Report edit state up so the page can warn before navigating away.
  useEffect(() => {
    onEditingChange(item.name, editing);
  }, [editing, item.name, onEditingChange]);
  useEffect(
    () => () => onEditingChange(item.name, false),
    [item.name, onEditingChange],
  );

  return (
    <Card>
      <CardActionArea
        onClick={() =>
          onOpenDetail({
            name: item.name,
            address: item.address,
            phone: item.phone,
            distance: '',
            tags: [],
            price: '',
            status: status === 'Secured' ? 'Available' : status,
          })
        }
      >
        <Box sx={{ p: 2 }}>
          <ProviderCardContent
            name={item.name}
            address={item.address}
            phone={item.phone}
            spaceBetweenHeader
            inlineChip={
              <StatusChipMenu
                status={status}
                onStatus={setStatus}
                onDismiss={() => onDismiss(item.name)}
              />
            }
          >
            <Stack sx={{ alignItems: 'flex-start', width: '100%' }} spacing={1}>
              {/* notes collapse to one line so the panel stays scannable; tap to
            read or edit */}
              {item.note && !editing && (
                <Box
                  onClick={() => setNoteOpen((v) => !v)}
                  sx={{ width: '100%', cursor: 'pointer' }}
                >
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    noWrap={!noteOpen}
                  >
                    Note: {item.note}
                  </Typography>
                </Box>
              )}

              {editing && (
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={2}
                  label='Note'
                  defaultValue={item.note}
                  placeholder='Add a note about this provider'
                />
              )}

              {editing ? (
                <Button
                  variant='contained'
                  size='small'
                  color='primary'
                  onClick={() => setEditing(false)}
                >
                  Save
                </Button>
              ) : !item.note ? (
                <Button
                  variant='text'
                  size='small'
                  color='primary'
                  onClick={() => setEditing(true)}
                >
                  Add note
                </Button>
              ) : noteOpen ? (
                <Button
                  variant='text'
                  size='small'
                  color='primary'
                  onClick={() => setEditing(true)}
                >
                  Edit note
                </Button>
              ) : null}
            </Stack>
          </ProviderCardContent>
        </Box>
      </CardActionArea>
    </Card>
  );
};

// A dismissed provider: red-badged, reason shown, restorable. Hidden from the
// care circle entirely (unlike 'Unavailable', which stays visible to them).
const DismissedCard = ({
  item,
  onRestore,
}: {
  item: ShortlistItem;
  onRestore: (name: string) => void;
}) => (
  <Card variant='padded'>
    <ProviderCardContent
      name={item.name}
      address={item.address}
      spaceBetweenHeader
      inlineChip={
        <Chip label='Dismissed' size='small' sx={badgeSx} color='error' />
      }
    >
      <Stack sx={{ alignItems: 'flex-start' }} spacing={1.5}>
        <Typography variant='body1' color='text.secondary'>
          {item.dismissed?.reason}
        </Typography>
        {item.dismissed?.detail && (
          <Typography variant='body1' color='text.secondary'>
            {item.dismissed.detail}
          </Typography>
        )}
        <Button
          variant='outlined'
          size='small'
          color='primary'
          onClick={() => onRestore(item.name)}
        >
          Restore
        </Button>
      </Stack>
    </ProviderCardContent>
  </Card>
);

// Dismiss with a reason: category plus optional detail. The format is an open
// PRD question — this prototypes the dropdown-plus-free-text option.
const DismissDialog = ({
  providerName,
  inFrame,
  onClose,
  onConfirm,
}: {
  providerName: string;
  inFrame?: boolean;
  onClose: () => void;
  onConfirm: (reason: string, detail: string) => void;
}) => {
  const [reason, setReason] = useState(DISMISS_REASONS[0]);
  const [detail, setDetail] = useState('');
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      {...(inFrame ? inFrameDialogProps : {})}
    >
      <DialogTitle>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
        >
          <Typography variant='h3' color='primary.main'>
            'Dismiss provider'
          </Typography>
          <IconButton aria-label='close' onClick={onClose}>
            <Icon icon='xmark' fontSize={18} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant='subtitle1'>
            ‘{providerName}’ will be hidden from the care circle and moved to
            the bottom of your list. You can restore it at any time.
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              label='Reason'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {DISMISS_REASONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label='Detail (optional)'
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' color='primary' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={() => onConfirm(reason, detail.trim())}
        >
          Dismiss provider
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// The paywall state per provider, keyed by the name this pane already uses.
//
// The two pages don't share live state — the shortlists tab is its own prototype
// with its own scene select — so the pane reads the scenario the tab opens on.
// That keeps one provider reading the same way on both screens, which is the
// point: a status a CAM cannot set should not look different depending on which
// surface they happen to be looking at.
const PANE_SCENARIO: Scenario = 'fresh';

const PANE_STATE: Record<string, PaywallState> = Object.fromEntries(
  providersFor(PANE_SCENARIO).map((p) => [p.name, p.state]),
);

// Anything on the list that the paywall model has not heard of — a provider
// added by hand in this session — has had nothing sent to it yet.
const PaneStatusChip = ({
  name,
  isPublished,
}: {
  name: string;
  isPublished: boolean;
}) => (
  <StatusChip state={PANE_STATE[name] ?? 'draft'} isPublished={isPublished} />
);

const ShortlistPane = ({
  shortlists,
  current,
  collapsed,
  initialShowDismissed,
  securedElsewhere,
  onToggle,
  onSelect,
  onCreate,
  onEditShortlist,
  onPublish,
  onMarkDirty,
  onEditingChange,
  onOpenDetail,
  onDismissItem,
  onRestoreItem,
}: {
  shortlists: Shortlist[];
  current: Shortlist | null;
  collapsed: boolean;
  initialShowDismissed?: boolean;
  // The care circle has arranged care outside these shortlists. Applies to the
  // whole pane, not the selected list, and stops anything else being published.
  securedElsewhere?: SecuredElsewhere | null;
  onToggle: () => void;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onEditShortlist: (id: string) => void;
  onPublish: () => void;
  // Flag the current shortlist as having unpublished edits, so a published
  // list re-offers "Publish changes" after an inline status/note change.
  onMarkDirty: () => void;
  onEditingChange: (name: string, editing: boolean) => void;
  onOpenDetail: (provider: Provider) => void;
  onDismissItem: (name: string) => void;
  onRestoreItem: (name: string) => void;
}) => {
  const [showDismissed, setShowDismissed] = useState(
    Boolean(initialShowDismissed),
  );

  // Which rows are open, keyed by provider name. A set rather than a single id
  // because comparing two providers' notes is the reason you'd open one at all.
  // Everything starts closed: the panel is a scan surface first.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRow = (name: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // Per-row note/status edit state for the basket variant, keyed by provider
  // name — lifted here so each basket row can be plain inline markup (no
  // per-row component). Falls back to the item's own status/note until edited.
  const [edits, setEdits] = useState<
    Record<
      string,
      { status: string; note: string; editing: boolean; draft: string }
    >
  >({});

  // Collapsed: a full-height vertical strip the width of just the chevron.
  if (collapsed) {
    return (
      <Box
        sx={{
          flexShrink: 0,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          bgcolor: 'custom.neutral.50',
          border: 1,
          borderColor: 'custom.neutral.200',
          borderRadius: `${radius.md}px`,
          color: 'text.primary',
        }}
        component='button'
        onClick={onToggle}
        aria-label='Expand shortlist'
        style={{ cursor: 'pointer', width: 44 }}
      >
        <Icon icon='chevron-left' fontSize={18} />
      </Box>
    );
  }

  // One shell for every state: same width, full column height, same header.
  // Only the body content changes between empty and populated.
  const hasList = shortlists.length > 0 && current !== null;
  const isPublished = current?.status === 'published';
  const activeItems = current?.items.filter((i) => !i.dismissed) ?? [];
  const dismissedItems = current?.items.filter((i) => i.dismissed) ?? [];

  const isBasket = true;
  const baseEdit = (item: ShortlistItem) => ({
    status: item.status ?? 'Not contacted',
    note: item.note ?? '',
    editing: false,
    draft: '',
  });
  const editFor = (item: ShortlistItem) => edits[item.name] ?? baseEdit(item);
  const patchEdit = (
    item: ShortlistItem,
    patch: Partial<ReturnType<typeof baseEdit>>,
  ) =>
    setEdits((prev) => ({
      ...prev,
      [item.name]: { ...(prev[item.name] ?? baseEdit(item)), ...patch },
    }));

  return (
    <Box
      sx={{
        flex: isBasket ? '0 0 420px' : 1,
        maxWidth: isBasket ? undefined : 460,
        minWidth: 0,
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'custom.neutral.50',
        border: 1,
        borderColor: 'custom.neutral.200',
        borderRadius: `${radius.md}px`,
        overflow: 'hidden',
      }}
    >
      <Stack sx={{ p: 2, flexShrink: 0 }} spacing={2}>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
        >
          <Stack
            sx={{ alignItems: 'center', minWidth: 0 }}
            direction='row'
            spacing={1}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: 0,
                p: 0,
                bgcolor: 'transparent',
                color: 'text.primary',
              }}
              component='button'
              onClick={onToggle}
              aria-label='Collapse shortlist'
              style={{ cursor: 'pointer' }}
            >
              <Icon icon='chevron-right' fontSize={18} />
            </Box>
            {/* 'Preview' rather than naming the member: the pane shows what the
                family will see, and the circle you are in already says whose it
                is. */}
            <Typography variant={isBasket ? 'h4' : 'h3'} noWrap>
              Preview
            </Typography>
          </Stack>
        </Stack>

        {/* declared on the member side: care arranged outside these homes. Sits
            above the list picker because it's true of the care circle, not of
            the selected shortlist. */}
        {securedElsewhere && (
          <Stack
            sx={{
              bgcolor: 'custom.limeGreen.100',
              borderRadius: `${radius.md}px`,
              p: 1.5,
            }}
            direction='row'
            spacing={1.5}
          >
            <Box
              sx={{ color: 'custom.limeGreen.800', display: 'flex', pt: 0.25 }}
            >
              <Icon icon='check-circle' fontSize={18} />
            </Box>
            <Stack sx={{ minWidth: 0 }} spacing={0.5}>
              <Typography variant='body2'>
                {SECURED_ELSEWHERE.title('Margarette')}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {SECURED_ELSEWHERE.detail(
                  securedElsewhere.by,
                  securedElsewhere.date,
                )}
              </Typography>
            </Stack>
          </Stack>
        )}

        {/* The same selector the shortlists tab uses, so a list is named and
            badged one way across CAM. Creating one is the item at the foot of its
            menu, which is why there is no plus in the header any more. */}
        {hasList && (
          <>
            <Stack
              sx={{ alignItems: 'center', minWidth: 0 }}
              direction='row'
              spacing={1}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ShortlistSelect
                  lists={shortlists}
                  value={current!.id}
                  onChange={onSelect}
                  onCreate={onCreate}
                  labels={{
                    draft: 'Draft',
                    published: 'Published',
                    create: 'Create shortlist',
                  }}
                />
              </Box>
              {/* Out of the menu rows and beside the select, acting on the list
                  that is open. In a row it was an action you could only reach by
                  opening the menu, on a list you were not looking at. */}
              <Link
                sx={{ flexShrink: 0 }}
                component='button'
                type='button'
                variant='body2'
                onClick={() => onEditShortlist(current!.id)}
              >
                Edit
              </Link>
            </Stack>

            {/* Publish stays visible but disables when there's nothing to
                publish: a clean published list, or care secured elsewhere (the
                search is over). It re-enables once the list is dirty (an
                unpublished edit) or it's still a draft. */}
            <Button
              variant='contained'
              color='primary'
              fullWidth
              disabled={
                Boolean(securedElsewhere) || (isPublished && !current!.dirty)
              }
              onClick={onPublish}
            >
              {isPublished ? 'Publish changes' : 'Publish'}
            </Button>
          </>
        )}
      </Stack>

      {!hasList && (
        <Stack sx={{ px: 2, pb: 2 }} spacing={2}>
          <Typography variant='body2' color='text.secondary'>
            Nothing here yet. Create a shortlist, or add a provider straight
            from the care search.
          </Typography>
          <Button
            variant='contained'
            color='primary'
            fullWidth
            onClick={onCreate}
          >
            Create shortlist
          </Button>
        </Stack>
      )}

      {/* list — active first, dismissed bumped to the bottom behind a toggle.
          Scrolls within the panel when it outgrows the viewport. */}
      {hasList && (
        <Box
          sx={{
            flex: 1,
            px: 2,
            pb: 2,
            pt: isBasket ? 1 : 0,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {activeItems.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              No care services have been added to this shortlist yet. Add
              providers from the care search to start building it.
            </Typography>
          ) : isBasket ? (
            <Stack divider={<Divider />}>
              {activeItems.map((item, i) => {
                const e = editFor(item);
                const open = expandedRows.has(item.name);
                // No spacing on the row: a collapsed Collapse is still a child,
                // so a gap here would pad every closed row. The expanded
                // content carries its own top padding instead.
                return (
                  <Stack sx={{ py: 2 }} key={`${item.name}-${i}`}>
                    {/* The row header carries only what you scan for: who it is
                        and where they've got to. The note lives behind the
                        chevron, so a long note can't push the next provider off
                        the panel. */}
                    <Stack
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                      direction='row'
                      spacing={1}
                    >
                      <Typography
                        variant='h6'
                        noWrap
                        onClick={() =>
                          onOpenDetail({
                            name: item.name,
                            address: item.address,
                            phone: item.phone,
                            distance: '',
                            tags: [],
                            price: '',
                            status:
                              e.status === 'Secured' ? 'Available' : e.status,
                          })
                        }
                      >
                        {item.name}
                      </Typography>
                      <Stack
                        sx={{ alignItems: 'center', flexShrink: 0 }}
                        direction='row'
                        spacing={0.5}
                      >
                        {/* The paywall status, read-only, from the same source
                            the shortlists tab reads. Not a menu any more: none of
                            these four states is something a CAM sets — the email
                            going out, Stripe confirming and the provider saying
                            no are what move them — so offering a dropdown taught
                            the wrong model of who is in control. Dismissing moved
                            into the expanded row, where the rest of the per-row
                            editing lives. */}
                        <PaneStatusChip
                          name={item.name}
                          isPublished={isPublished}
                        />
                        {/* Expands the row rather than editing it — the pen
                            moved inside, so the header has one job. */}
                        <IconButton
                          size='small'
                          aria-label={open ? 'Collapse row' : 'Expand row'}
                          onClick={() => toggleRow(item.name)}
                        >
                          <Icon
                            icon={open ? 'chevron-up' : 'chevron-down'}
                            fontSize={14}
                          />
                        </IconButton>
                      </Stack>
                    </Stack>

                    <Collapse in={open}>
                      {/* pt keeps the note off the header without adding a gap
                          to a collapsed row */}
                      <Stack
                        sx={{ alignItems: 'flex-start', pt: 0.5 }}
                        spacing={1}
                      >
                        {e.editing ? (
                          <>
                            <TextField
                              autoFocus
                              fullWidth
                              multiline
                              minRows={2}
                              size='small'
                              value={e.draft}
                              onChange={(ev) =>
                                patchEdit(item, { draft: ev.target.value })
                              }
                              placeholder='Add a note about this provider'
                            />
                            <Button
                              variant='contained'
                              size='small'
                              color='primary'
                              onClick={() => {
                                patchEdit(item, {
                                  note: e.draft.trim(),
                                  editing: false,
                                });
                                onMarkDirty();
                              }}
                            >
                              Save
                            </Button>
                          </>
                        ) : (
                          <>
                            {e.note && (
                              <Typography
                                variant='body1'
                                color='text.secondary'
                              >
                                {e.note}
                              </Typography>
                            )}
                            <Stack direction='row' spacing={1}>
                              <Button
                                variant='text'
                                size='small'
                                color='primary'
                                onClick={() =>
                                  patchEdit(item, {
                                    editing: true,
                                    draft: e.note,
                                  })
                                }
                              >
                                {e.note ? 'Edit note' : 'Add note'}
                              </Button>
                              {/* Named and coloured like the dialog it opens, so
                                  the click and the confirm are recognisably one
                                  act. 'Dismiss' alone next to 'Edit note' read as
                                  dismissing the note. */}
                              <Button
                                variant='text'
                                size='small'
                                color='error'
                                onClick={() => onDismissItem(item.name)}
                              >
                                Dismiss provider
                              </Button>
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </Collapse>
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {activeItems.map((item, i) => (
                <ShortlistCard
                  key={`${item.name}-${i}`}
                  item={item}
                  onEditingChange={onEditingChange}
                  onOpenDetail={onOpenDetail}
                  onDismiss={onDismissItem}
                />
              ))}
            </Stack>
          )}

          {showDismissed && dismissedItems.length > 0 && (
            <Stack sx={{ mt: 2, alignItems: 'flex-start' }} spacing={2}>
              {dismissedItems.map((item, i) =>
                isBasket ? (
                  <Stack
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                    key={`${item.name}-${i}`}
                    direction='row'
                    spacing={1}
                  >
                    <Typography variant='body2' color='text.secondary' noWrap>
                      {item.name}
                    </Typography>
                    <Link
                      component='button'
                      type='button'
                      variant='body2'
                      onClick={() => onRestoreItem(item.name)}
                    >
                      Restore
                    </Link>
                  </Stack>
                ) : (
                  <Box sx={{ width: '100%' }} key={`${item.name}-${i}`}>
                    <DismissedCard item={item} onRestore={onRestoreItem} />
                  </Box>
                ),
              )}
            </Stack>
          )}
        </Box>
      )}

      {/* dismissed toggle — pinned to the bottom of the panel, below the
          scrolling list */}
      {hasList && dismissedItems.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 0.75,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Link
            component='button'
            type='button'
            variant='caption'
            color='primary'
            onClick={() => setShowDismissed((v) => !v)}
          >
            {showDismissed
              ? 'Hide dismissed'
              : `Show dismissed (${dismissedItems.length})`}
          </Link>
        </Box>
      )}
    </Box>
  );
};

// ─── provider detail (overlay) ──────────────────────────────────────────────────

const ratingColor = (r: string): 'success' | 'limeGreen' | 'warmOrange' =>
  r === 'Outstanding' ? 'success' : r === 'Good' ? 'limeGreen' : 'warmOrange';

const Stat = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body1'>{value}</Typography>
  </Box>
);

// ─── notes (global, CAM-only) ───────────────────────────────────────────────────

const NoteItem = ({
  note,
  onSave,
  onDelete,
}: {
  note: ProviderNote;
  onSave: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.text);
  const mine = note.author === CURRENT_CAM;

  return (
    <Stack
      sx={{
        p: 1.5,
        mb: 1.5,
        borderRadius: '8px',
        bgcolor: 'custom.neutral.100',
      }}
      spacing={0.5}
    >
      <Stack
        sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        direction='row'
        spacing={1}
      >
        <Typography variant='caption' color='text.secondary'>
          {note.author} · {note.date}
        </Typography>
        {mine && !editing && (
          <Stack direction='row' spacing={0.5}>
            <Button
              variant='text'
              size='small'
              color='primary'
              onClick={() => {
                setDraft(note.text);
                setEditing(true);
              }}
            >
              Edit
            </Button>
            <Button
              variant='text'
              size='small'
              color='primary'
              onClick={() => onDelete(note.id)}
            >
              Delete
            </Button>
          </Stack>
        )}
      </Stack>
      {editing ? (
        <Stack spacing={1}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
          <Stack direction='row' spacing={1}>
            <Button
              variant='contained'
              size='small'
              color='primary'
              disabled={!draft.trim()}
              onClick={() => {
                onSave(note.id, draft.trim());
                setEditing(false);
              }}
            >
              Save
            </Button>
            <Button
              variant='text'
              size='small'
              color='primary'
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Typography variant='body1'>{note.text}</Typography>
      )}
    </Stack>
  );
};

// The global note feed plus composer, shared by the detail page's rail and the
// result-card dialog. The feed caps and scrolls: both callers give it a card
// sized by its own content, so a long note list has to stay inside it.
const NotesPanel = ({
  notes,
  onAdd,
  onSave,
  onDelete,
}: {
  notes: ProviderNote[];
  onAdd: (text: string) => void;
  onSave: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}) => {
  const [draft, setDraft] = useState('');
  const list = (
    <>
      {notes.length === 0 && (
        <Typography variant='body2' color='text.secondary'>
          No notes on this provider yet.
        </Typography>
      )}
      {notes.map((n) => (
        <NoteItem key={n.id} note={n} onSave={onSave} onDelete={onDelete} />
      ))}
    </>
  );
  return (
    <Stack spacing={1.5}>
      <Box sx={{ maxHeight: 320, overflow: 'auto' }}>{list}</Box>
      <TextField
        fullWidth
        multiline
        minRows={3}
        placeholder='Add an internal note about this provider (e.g. “very responsive manager”)'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <Button
        variant='contained'
        size='small'
        color='primary'
        style={{ alignSelf: 'flex-start' }}
        disabled={!draft.trim()}
        onClick={() => {
          onAdd(draft.trim());
          setDraft('');
        }}
      >
        Save note
      </Button>
    </Stack>
  );
};

// Global provider notes opened from 'Add note to provider' on a result card.
const ProviderNotesDialog = ({
  provider,
  notes,
  inFrame,
  onClose,
  onAdd,
  onSave,
  onDelete,
}: {
  provider: Provider;
  notes: ProviderNote[];
  inFrame?: boolean;
  onClose: () => void;
  onAdd: (text: string) => void;
  onSave: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}) => (
  <Dialog
    open
    onClose={onClose}
    maxWidth='sm'
    fullWidth
    {...(inFrame ? inFrameDialogProps : {})}
  >
    <DialogTitle>
      <Stack
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        direction='row'
      >
        <Typography variant='h3' color='primary.main'>
          {`Notes · ${provider.name}`}
        </Typography>
        <IconButton aria-label='close' onClick={onClose}>
          <Icon icon='xmark' fontSize={18} />
        </IconButton>
      </Stack>
    </DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant='subtitle1'>
          Visible to other CAMs, not the care circle.
        </Typography>
        <NotesPanel
          notes={notes}
          onAdd={onAdd}
          onSave={onSave}
          onDelete={onDelete}
        />
      </Stack>
    </DialogContent>
  </Dialog>
);

const CareDetail = ({
  provider,
  shortlists,
  selectedId,
  notes,
  onAddNote,
  onSaveNote,
  onDeleteNote,
  onAdd,
  onCreateNew,
  onBack,
}: {
  provider: Provider;
  shortlists: Shortlist[];
  selectedId: string | null;
  notes: ProviderNote[];
  onAddNote: (text: string) => void;
  onSaveNote: (id: string, text: string) => void;
  onDeleteNote: (id: string) => void;
  onAdd: (provider: Provider, shortlistId: string) => void;
  onCreateNew: (provider: Provider) => void;
  onBack: () => void;
}) => {
  return (
    <ShellOverlay onBack={onBack}>
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        {/* Hero: who this is, and the rating. Cost moved into the first
            card, so what is left here is identity. */}
        <Stack
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 3,
          }}
          direction='row'
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant='h3' color='primary.main'>
              {provider.name}
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              {provider.address}
            </Typography>
            {/* Named, not just coloured. On its own under the address a bare
                  "Good" is good at nothing in particular, and the regulator is
                  the only thing on this page rated in those words. */}
            <Box sx={{ pt: 1.5 }}>
              <Chip
                label={`CQC · ${PROVIDER_DETAIL.rating}`}
                size='small'
                color={ratingColor(PROVIDER_DETAIL.rating)}
              />
            </Box>
          </Box>
          <AddToShortlistMenuButton
            provider={provider}
            shortlists={shortlists}
            selectedId={selectedId}
            onAdd={onAdd}
            onCreateNew={onCreateNew}
          />
        </Stack>

        {/* One reading column with the notes pinned beside it. Lottie floats
              its booking card there; for a care expert the equivalent is what
              we already know about this home, which has to stay in reach while
              you read the rest. Compact drops the rail under the column. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 340px',
            gap: 3,
            alignItems: 'start',
            [CAM_COMPACT]: { gridTemplateColumns: 'minmax(0, 1fr)' },
          }}
        >
          <Stack spacing={2}>
            {/* What this home offers and what it costs, first. The two
                  questions a care expert is answering for a family are whether
                  the home does the kind of care they need and whether they can
                  pay for it, so neither is worth scrolling for. */}
            <Card variant='padded'>
              <Stack spacing={1.5}>
                <Typography variant='h6' color='primary.main'>
                  Care available
                </Typography>
                <Stack
                  sx={{ flexWrap: 'wrap' }}
                  direction='row'
                  spacing={1}
                  useFlexGap
                >
                  {PROVIDER_DETAIL.careTypes.map((c) => (
                    <Chip key={c} label={c} size='small' />
                  ))}
                </Stack>
                <Divider />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.5,
                  }}
                >
                  <Stat
                    label='Care from'
                    value={provider.price || PROVIDER_DETAIL.fallbackPrice}
                  />
                  <Stat
                    label='Availability'
                    value={
                      provider.status || PROVIDER_DETAIL.fallbackAvailability
                    }
                  />
                </Box>
              </Stack>
            </Card>

            {/* Photos next, near the top as Lottie has them: what the place
                  looks like is the fastest thing to take in. */}
            <Card variant='padded'>
              <Stack spacing={1.5}>
                <Typography variant='h6' color='primary.main'>
                  Photos
                </Typography>
                <Box
                  sx={{
                    height: 260,
                    borderRadius: `${radius.md}px`,
                    bgcolor: 'custom.neutral.200',
                  }}
                />
                <Stack direction='row' spacing={1.5}>
                  {[0, 1, 2, 3].map((i) => (
                    <Box
                      sx={{
                        flex: 1,
                        height: 72,
                        borderRadius: '8px',
                        bgcolor: 'custom.neutral.200',
                      }}
                      key={i}
                    />
                  ))}
                </Stack>
              </Stack>
            </Card>

            <Card variant='padded'>
              <Stack spacing={1.5}>
                <Typography variant='h6' color='primary.main'>
                  Overview
                </Typography>
                <Typography
                  sx={{ fontStyle: 'italic' }}
                  variant='body1'
                  color='text.secondary'
                >
                  {PROVIDER_DETAIL.ownerQuote}
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  {PROVIDER_DETAIL.ownerDescription}
                </Typography>
                <Divider />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 1.5,
                  }}
                >
                  <Stat label='Beds' value={PROVIDER_DETAIL.beds} />
                  <Stat label='Manager' value={PROVIDER_DETAIL.manager} />
                  <Stat label='Visiting' value={PROVIDER_DETAIL.visiting} />
                </Box>
              </Stack>
            </Card>

            {/* What the headline price does not cover. From and Availability
                  are up in the first card, so repeating them here would make
                  three stats out of one new fact. */}
            <Card variant='padded'>
              <Stack spacing={1.5}>
                <Typography variant='h6' color='primary.main'>
                  Pricing
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.5,
                  }}
                >
                  <Stat
                    label='Respite stays'
                    value={PROVIDER_DETAIL.respitePrice}
                  />
                </Box>
              </Stack>
            </Card>

            {/* The overall rating and what it is made of, in one card. Two
                  cards side by side asked which was the real rating. */}
            <Card variant='padded'>
              <Stack spacing={1.5}>
                <Typography variant='h6' color='primary.main'>
                  Regulator rating
                </Typography>
                <Stack
                  sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                  direction='row'
                  spacing={1}
                  useFlexGap
                >
                  <Chip
                    label={PROVIDER_DETAIL.rating}
                    color={ratingColor(PROVIDER_DETAIL.rating)}
                  />
                  <Typography variant='body2' color='text.secondary'>
                    {PROVIDER_DETAIL.regulatorLine}
                  </Typography>
                </Stack>
                <Divider />
                <Stack spacing={1}>
                  {PROVIDER_DETAIL.inspection.map((a) => (
                    <Stack
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      key={a.area}
                      direction='row'
                    >
                      <Typography variant='body1'>{a.area}</Typography>
                      <Chip
                        label={a.rating}
                        size='small'
                        color={ratingColor(a.rating)}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Card>

            <Card variant='padded'>
              <Stack spacing={1.5}>
                <Typography variant='h6' color='primary.main'>
                  Funding accepted
                </Typography>
                <Typography variant='body1'>
                  {PROVIDER_DETAIL.fundingAccepted}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {PROVIDER_DETAIL.fundingNote}
                </Typography>
              </Stack>
            </Card>

            <Card variant='padded'>
              <Stack spacing={1.5}>
                <Typography variant='h6' color='primary.main'>
                  Facilities
                </Typography>
                <Box sx={{ m: 0, pl: 3 }} component='ul'>
                  {PROVIDER_DETAIL.features.map((f) => (
                    <Typography
                      key={f}
                      component='li'
                      variant='body1'
                      color='text.secondary'
                    >
                      {f}
                    </Typography>
                  ))}
                </Box>
              </Stack>
            </Card>

            {/* Location last but one, as Lottie has it. The phone number is
                  here rather than in the rail: the rail is the notes now, and
                  a number is a fact about the home like its address. */}
            <Card variant='padded'>
              <Stack spacing={1.5}>
                <Typography variant='h6' color='primary.main'>
                  Location and contact
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  {provider.address}
                </Typography>
                <Divider />
                <Stack spacing={0.5}>
                  <Link
                    href={`tel:${(
                      provider.phone || PROVIDER_DETAIL.fallbackPhone
                    ).replace(/\s/g, '')}`}
                    variant='body1'
                  >
                    {provider.phone || PROVIDER_DETAIL.fallbackPhone}
                  </Link>
                  <Typography variant='body1' color='text.secondary'>
                    {PROVIDER_DETAIL.website}
                  </Typography>
                </Stack>
              </Stack>
            </Card>
          </Stack>

          {/* The rail. Sticks to the top of the scroller, so the notes are
                still there nine sections down. Content height with the list
                capped, rather than filling the row: a rail as tall as the page
                is a column of empty card. */}
          <Box sx={{ position: 'sticky', top: 0 }}>
            <Card variant='padded'>
              <Stack spacing={1.5}>
                <Typography variant='h6' color='primary.main'>
                  Notes
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Visible to other CAMs, not the care circle.
                </Typography>
                <NotesPanel
                  notes={notes}
                  onAdd={onAddNote}
                  onSave={onSaveNote}
                  onDelete={onDeleteNote}
                />
              </Stack>
            </Card>
          </Box>
        </Box>
      </Box>
    </ShellOverlay>
  );
};

// The CAM care search filter bar: the search field (with a Clear-all link) and
// the grid of filter selects (care type, rating). Named so it reads as one
// layer in drill mode rather than a loose Stack of inputs.
// One fixed width for every select on the filter bar, so they line up tidily.
const SELECT_W = 180;

const CareSearchFilters = ({
  onSearch,
  careType,
  onCareType,
  filters,
  onFilter,
  showClear,
  onClearAll,
}: {
  onSearch: () => void;
  careType: CareTypeId;
  onCareType: (id: CareTypeId) => void;
  filters: Record<string, string>;
  onFilter: (id: string, value: string) => void;
  showClear: boolean;
  onClearAll: () => void;
}) => (
  <Stack spacing={1.5}>
    {/* Search, care type and Clear all on one row, then the filters under it —
        the same two-row split as the shipped toolbar in
        lib/llama/src/pwa/organisms/CareProviderResults/CareSearchFilters.tsx. */}
    <Stack
      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      direction='row'
      spacing={1.5}
    >
      {/* search — width owned by the layout wrapper, not an sx on the field.
          The pill and the adornment live in SearchField. */}
      <Box sx={{ width: 320 }}>
        <SearchField
          placeholder='Search by name or location'
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearch();
            }
          }}
        />
      </Box>

      {/* care type — the primary split. Switching it swaps the filters and sort
          options, mirroring Care Sourcer. A toggle rather than a select because
          there are two answers and production shows both: a select would hide
          one of them behind a click. Stock MUI, no `segmented` class, so it is
          the control that ships rather than the brand segmented one. */}
      <ToggleButtonGroup
        size='xsmall'
        exclusive
        value={careType}
        onChange={(_, v) => v != null && onCareType(v as CareTypeId)}
      >
        {CARE_TYPES.map((c) => (
          <ToggleButton key={c.id} value={c.id}>
            {c.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {showClear && (
        <Link
          component='button'
          type='button'
          variant='body2'
          onClick={onClearAll}
        >
          Clear all
        </Link>
      )}
    </Stack>

    {/* The filter selects, packed left. */}
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}
    >
      {FILTERS_BY_CARE_TYPE[careType].map((f) => (
        <FormControl key={f.id} size='xsmall' sx={{ width: SELECT_W }}>
          <Select
            value={filters[f.id]}
            onChange={(e) => onFilter(f.id, e.target.value)}
            displayEmpty
            renderValue={() => filters[f.id] || f.label}
          >
            <MenuItem value=''>
              <em>Any</em>
            </MenuItem>
            {f.options.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}
    </Box>
  </Stack>
);

const CareSearch = ({
  initial,
  embedded = false,
}: {
  initial?: CareSearchInitial;
  embedded?: boolean;
}) => {
  const startScenario = initial?.scenario ?? 'draft';
  const start = scenarioState(startScenario);
  // In a mini, an auto-focused seed note would grab focus and scroll the
  // handover page, so seeded notes start closed.
  const startShortlists = embedded
    ? start.shortlists.map((s) => ({
        ...s,
        items: s.items.map((item) => ({ ...item, editingInitial: false })),
      }))
    : start.shortlists;
  // Declared by the carer on the member side, so the CAM only reads it here.
  const securedElsewhere = start.securedElsewhere ?? null;
  const [searched, setSearched] = useState(start.searched);
  const [shortlists, setShortlists] = useState<Shortlist[]>(startShortlists);
  const [selectedId, setSelectedId] = useState<string | null>(
    startShortlists[0]?.id ?? null,
  );
  const [collapsed, setCollapsed] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    onUndo?: () => void;
  } | null>(null);
  const [editingItems, setEditingItems] = useState<string[]>([]);
  const [modal, setModal] = useState<{
    mode: 'create' | 'edit';
    provider?: Provider;
  } | null>(initial?.modal ? { mode: initial.modal } : null);
  const [publishScope, setPublishScope] = useState<'one' | null>(
    initial?.publish ?? null,
  );
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    RESULTS.find((p) => p.name === initial?.detailProvider) ?? null,
  );
  const [dismissTarget, setDismissTarget] = useState<string | null>(
    initial?.dismiss ?? null,
  );
  const [notesTarget, setNotesTarget] = useState<Provider | null>(
    RESULTS.find((p) => p.name === initial?.notes) ?? null,
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Global provider notes, keyed by provider name. Shared across search
  // results, shortlists and the detail page.
  const [notesByProvider, setNotesByProvider] = useState<
    Record<string, ProviderNote[]>
  >({ 'Oakwood care home': SEED_NOTES });
  const idRef = useRef(0);

  // ── search pane (laid out inline below, like the rest of the page) ──
  const [view, setView] = useState<'list' | 'map'>(initial?.view ?? 'list');
  const [careType, setCareType] = useState<CareTypeId>(INITIAL_CARE_TYPE);
  const [sort, setSort] = useState<SortOption>('Most relevant');
  const [filters, setFilters] =
    useState<Record<string, string>>(INITIAL_FILTERS);
  const setFilter = (id: string, value: string) =>
    setFilters((prev) => ({ ...prev, [id]: value }));
  const clearFilters = () =>
    setFilters(Object.fromEntries(ALL_FILTER_IDS.map((id) => [id, ''])));
  // Switching care type swaps the available filters and sort options, so reset
  // the sort back to relevance (Care Sourcer does the same on care-type change).
  const changeCareType = (id: CareTypeId) => {
    setCareType(id);
    setSort('Most relevant');
  };
  const sortOptions = SORT_OPTIONS_BY_CARE_TYPE[careType];
  // Only the filters visible for the current care type count toward "active",
  // so Clear all / the clear link reflect what's actually on screen.
  const activeFilters = FILTERS_BY_CARE_TYPE[careType].filter(
    (f) => filters[f.id],
  );
  const results = [...RESULTS].sort(SORTERS[sort]);

  const current = shortlists.find((s) => s.id === selectedId) ?? null;

  // Switch the viewed shortlist and announce it.
  const selectShortlist = (id: string) => {
    if (id === selectedId) return;
    const sl = shortlists.find((s) => s.id === id);
    setSelectedId(id);
    if (sl) setToast({ message: `You are now working on ‘${sl.name}’` });
  };

  const addToShortlist = (provider: Provider, shortlistId: string) => {
    setShortlists((prev) =>
      prev.map((s) =>
        s.id === shortlistId
          ? { ...s, items: [...s.items, toItem(provider)], dirty: true }
          : s,
      ),
    );
    setSelectedId(shortlistId);
    const sl = shortlists.find((s) => s.id === shortlistId);
    setToast({
      message: `Added ‘${provider.name}’ to ‘${sl?.name ?? 'shortlist'}’`,
      // Drop the provider we just appended (the last match by name).
      onUndo: () => {
        setShortlists((prev) =>
          prev.map((s) => {
            if (s.id !== shortlistId) return s;
            const idx = s.items.map((i) => i.name).lastIndexOf(provider.name);
            return idx === -1
              ? s
              : { ...s, items: s.items.filter((_, i) => i !== idx) };
          }),
        );
        setToast(null);
      },
    });
  };

  const openCreate = (provider?: Provider) =>
    setModal({ mode: 'create', provider });
  const openEdit = () => setModal({ mode: 'edit' });

  // ── global provider notes ──
  const notesFor = (name: string) => notesByProvider[name] ?? [];
  const addNote = (name: string, text: string) =>
    setNotesByProvider((prev) => ({
      ...prev,
      [name]: [
        {
          id: `${name}-${(prev[name]?.length ?? 0) + 1}`,
          author: CURRENT_CAM,
          date: today(),
          text,
        },
        ...(prev[name] ?? []),
      ],
    }));
  const saveNote = (name: string, id: string, text: string) =>
    setNotesByProvider((prev) => ({
      ...prev,
      [name]: (prev[name] ?? []).map((n) => (n.id === id ? { ...n, text } : n)),
    }));
  const deleteNote = (name: string, id: string) =>
    setNotesByProvider((prev) => ({
      ...prev,
      [name]: (prev[name] ?? []).filter((n) => n.id !== id),
    }));

  // ── dismiss / restore / delete ──
  const dismissItem = (reason: string, detail: string) => {
    if (!dismissTarget) return;
    setShortlists((prev) =>
      prev.map((s) =>
        s.id === selectedId
          ? {
              ...s,
              dirty: true,
              items: s.items.map((it) =>
                it.name === dismissTarget
                  ? {
                      ...it,
                      dismissed: {
                        reason,
                        detail: detail || undefined,
                      },
                    }
                  : it,
              ),
            }
          : s,
      ),
    );
    setToast({
      message: `Dismissed ‘${dismissTarget}’. The care circle won’t see it`,
    });
    setDismissTarget(null);
  };

  const restoreItem = (name: string) => {
    setShortlists((prev) =>
      prev.map((s) =>
        s.id === selectedId
          ? {
              ...s,
              dirty: true,
              items: s.items.map((it) =>
                it.name === name ? { ...it, dismissed: undefined } : it,
              ),
            }
          : s,
      ),
    );
    setToast({
      message: `Restored ‘${name}’ to ‘${current?.name ?? 'the shortlist'}’`,
    });
  };

  const deleteShortlist = () => {
    const name = current?.name;
    const remaining = shortlists.filter((s) => s.id !== selectedId);
    setShortlists(remaining);
    setSelectedId(remaining[0]?.id ?? null);
    setDeleteOpen(false);
    setToast({ message: `Deleted ‘${name ?? 'shortlist'}’` });
  };

  const submitModal = (name: string) => {
    if (!modal) return;
    if (modal.mode === 'edit') {
      setShortlists((prev) =>
        prev.map((s) =>
          s.id === selectedId ? { ...s, name, dirty: true } : s,
        ),
      );
    } else {
      const id = `new${(idRef.current += 1)}`;
      const items = modal.provider ? [toItem(modal.provider)] : [];
      setShortlists((prev) => [...prev, { id, name, status: 'draft', items }]);
      setSelectedId(id);
      setSearched(true);
      setToast({
        message: modal.provider
          ? `Added ‘${modal.provider.name}’ to ‘${name}’`
          : `Created ‘${name}’`,
      });
    }
    setModal(null);
  };

  const confirmPublish = () => {
    const wasPublished = current?.status === 'published';
    setShortlists((prev) =>
      prev.map((s) =>
        s.id === selectedId ? { ...s, status: 'published', dirty: false } : s,
      ),
    );
    setToast({
      message: wasPublished
        ? 'Changes published — the care circle has been notified'
        : 'Shortlist published — the care circle can now see it',
    });
    setPublishScope(null);
  };

  const handleEditingChange = useCallback((name: string, editing: boolean) => {
    setEditingItems((prev) =>
      editing
        ? prev.includes(name)
          ? prev
          : [...prev, name]
        : prev.filter((n) => n !== name),
    );
  }, []);

  // Native warning for a browser refresh/close while a note is unsaved.
  // Skipped when embedded: a handover page shouldn't guard the browser tab.
  useEffect(() => {
    if (embedded || editingItems.length === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [embedded, editingItems.length]);

  const defaultName = `Shortlist ${shortlists.length + 1} - 1/06/2026`;
  const modalInitialName =
    modal?.mode === 'edit' ? (current?.name ?? '') : defaultName;
  const isRepublish = publishScope === 'one' && current?.status === 'published';

  // Content only — the control panel shell (ControlPanel) provides the app
  // bar, nav rail, header and tab strip around this.
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {/* two-pane body (fills remaining height; each pane scrolls
          independently). Compact: the panes stack, the shortlist below the
          results, each still scrolling within its own band.

          It stays mounted while a provider is open, so Back drops you exactly
          where you were: same scroll, same filters, same shortlist. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          gap: 3,
          mt: 3,
          overflow: 'hidden',
          alignItems: 'stretch',
          [CAM_COMPACT]: { flexDirection: 'column', gap: 2 },
        }}
      >
        {/* left: search — search field + filter grid, then results as a
              card grid or the mock map */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', pr: 1 }}>
          <Stack spacing={2}>
            <CareSearchFilters
              onSearch={() => setSearched(true)}
              careType={careType}
              onCareType={changeCareType}
              filters={filters}
              onFilter={setFilter}
              showClear={activeFilters.length > 0}
              onClearAll={clearFilters}
            />

            {searched ? (
              <>
                <Stack
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  direction='row'
                >
                  <Typography variant='subtitle2'>
                    Displaying {results.length} results
                  </Typography>
                  <Stack
                    sx={{ alignItems: 'center' }}
                    direction='row'
                    spacing={1.5}
                  >
                    <FormControl size='xsmall' style={{ width: 196 }}>
                      <Select
                        value={sort}
                        onChange={(e) => setSort(e.target.value as SortOption)}
                        renderValue={() => `Sort: ${sort}`}
                      >
                        {sortOptions.map((o) => (
                          <MenuItem key={o} value={o}>
                            {o}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <ToggleButtonGroup
                      exclusive
                      size='small'
                      value={view}
                      onChange={(_, v) => v != null && setView(v)}
                    >
                      <ToggleButton value='list'>List</ToggleButton>
                      <ToggleButton value='map'>Map</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                </Stack>

                {view === 'map' ? (
                  <MapView
                    results={results}
                    onOpenDetail={setSelectedProvider}
                  />
                ) : (
                  /* horizontal media cards, one per row: photo left, details
                        right, add pinned to the top-right corner */
                  <Stack spacing={2}>
                    {results.map((p) => {
                      const grade = careRatingGrade(p);
                      // The shortlist this provider is already on, if any.
                      // Drives the Featured pill, so a result you've already
                      // dealt with says so before you read the name twice.
                      const featuredOn = shortlists.find((s) =>
                        s.items.some((i) => i.name === p.name && !i.dismissed),
                      );
                      return (
                        <Box sx={{ position: 'relative' }} key={p.name}>
                          <Card>
                            <CardActionArea
                              onClick={() => setSelectedProvider(p)}
                            >
                              <CardContent>
                                <Stack
                                  sx={{ alignItems: 'flex-start' }}
                                  direction='row'
                                  spacing={2}
                                >
                                  <CardMedia
                                    component='img'
                                    image='/vector/photo-placeholder.svg'
                                    alt=''
                                    sx={{
                                      width: 96,
                                      height: 96,
                                      borderRadius: '8px',
                                      objectFit: 'cover',
                                      flexShrink: 0,
                                    }}
                                  />
                                  {/* pr keeps the longest name clear of the
                                        add button's corner */}
                                  <Stack
                                    sx={{ flex: 1, minWidth: 0, pr: 4 }}
                                    spacing={1.5}
                                  >
                                    <Stack spacing={1}>
                                      <Typography variant='h4'>
                                        {p.name}
                                      </Typography>
                                      <Typography
                                        variant='body1'
                                        color='text.secondary'
                                      >
                                        {p.address}
                                      </Typography>
                                    </Stack>
                                    <Stack
                                      sx={{
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                      }}
                                      direction='row'
                                      spacing={1}
                                      useFlexGap
                                    >
                                      {featuredOn && (
                                        <Chip
                                          size='small'
                                          color='primary'
                                          sx={badgeSx}
                                          label={`Featured on ${featuredOn.name}`}
                                        />
                                      )}
                                      <Chip
                                        size='small'
                                        variant='outlined'
                                        label={careTypeOf(p)}
                                        sx={badgeSx}
                                      />
                                      {grade && (
                                        <Chip
                                          size='small'
                                          color='success'
                                          sx={badgeSx}
                                          label={`CQC: ${grade}`}
                                        />
                                      )}
                                    </Stack>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                          {/* Outside the CardActionArea on purpose: a button
                                inside a button is invalid, and adding to a
                                shortlist shouldn't also open the detail page. */}
                          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <AddToShortlistMenuButton
                              icon
                              provider={p}
                              shortlists={shortlists}
                              selectedId={selectedId}
                              onAdd={addToShortlist}
                              onCreateNew={openCreate}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <Box sx={{ mb: 2, color: 'custom.neutral.500' }}>
                  <Icon icon='magnifying-glass' fontSize={32} />
                </Box>
                <Typography sx={{ mb: 1 }} variant='h4'>
                  Search for care services
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  Use the search and filters above to find residential, nursing
                  or at-home care near Margarette.
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
        <ShortlistPane
          shortlists={shortlists}
          current={current}
          collapsed={collapsed}
          initialShowDismissed={initial?.showDismissed}
          securedElsewhere={securedElsewhere}
          onToggle={() => setCollapsed((c) => !c)}
          onSelect={selectShortlist}
          onCreate={() => openCreate()}
          onEditShortlist={(id) => {
            selectShortlist(id);
            openEdit();
          }}
          onPublish={() => setPublishScope('one')}
          onMarkDirty={() =>
            setShortlists((prev) =>
              prev.map((s) =>
                s.id === selectedId ? { ...s, dirty: true } : s,
              ),
            )
          }
          onEditingChange={handleEditingChange}
          onOpenDetail={setSelectedProvider}
          onDismissItem={setDismissTarget}
          onRestoreItem={restoreItem}
        />
      </Box>

      {/* a provider — over the whole shell, sliding up from the bottom */}
      {selectedProvider && (
        <CareDetail
          provider={selectedProvider}
          shortlists={shortlists}
          selectedId={selectedId}
          notes={notesFor(selectedProvider.name)}
          onAddNote={(text) => addNote(selectedProvider.name, text)}
          onSaveNote={(id, text) => saveNote(selectedProvider.name, id, text)}
          onDeleteNote={(id) => deleteNote(selectedProvider.name, id)}
          onAdd={addToShortlist}
          onCreateNew={openCreate}
          onBack={() => setSelectedProvider(null)}
        />
      )}

      {/* create / edit shortlist modal */}
      {modal && (
        <ShortlistModal
          key={`${modal.mode}-${modal.provider?.name ?? ''}`}
          open
          mode={modal.mode}
          initialName={modalInitialName}
          inFrame={embedded}
          onClose={() => setModal(null)}
          onSubmit={submitModal}
        />
      )}

      {/* publish review / confirm */}
      <Dialog
        open={Boolean(publishScope)}
        onClose={() => setPublishScope(null)}
        maxWidth='xs'
        fullWidth
        {...(embedded ? inFrameDialogProps : {})}
      >
        <DialogTitle>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            direction='row'
          >
            <Typography variant='h3' color='primary.main'>
              {isRepublish ? 'Publish changes' : 'Publish shortlist'}
            </Typography>
            <IconButton
              aria-label='close'
              onClick={() => setPublishScope(null)}
            >
              <Icon icon='xmark' fontSize={18} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {/* One sentence and the ask. It used to count the providers, name the
              circle, promise a notification and an action, and cover dismissed
              providers and editing afterwards — six facts in front of a
              decision that is one. The count is on the list behind the modal,
              and notes are the part a CAM would not otherwise expect to
              travel. */}
          <Typography variant='subtitle1'>
            {isRepublish ? (
              <>
                ‘{current?.name}’ has unpublished changes. Publishing them
                notifies Margarette’s care circle that there’s something new to
                look at.
              </>
            ) : (
              <>
                Publishing makes a shortlist, including shortlist notes, visible
                to the care circle. Please confirm to continue.
              </>
            )}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => setPublishScope(null)}
          >
            Cancel
          </Button>
          <Button variant='contained' color='primary' onClick={confirmPublish}>
            {isRepublish ? 'Publish changes' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth='xs'
        fullWidth
        {...(embedded ? inFrameDialogProps : {})}
      >
        <DialogTitle>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            direction='row'
          >
            <Typography variant='h3' color='primary.main'>
              Delete shortlist
            </Typography>
            <IconButton aria-label='close' onClick={() => setDeleteOpen(false)}>
              <Icon icon='xmark' fontSize={18} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Typography variant='subtitle1'>
            ‘{current?.name}’ and its notes will be deleted. The care circle
            will no longer see it. This can’t be undone.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => setDeleteOpen(false)}
          >
            Cancel
          </Button>
          <Button variant='contained' color='error' onClick={deleteShortlist}>
            Delete shortlist
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.onUndo ? 6000 : 3000}
        onClose={() => setToast(null)}
        message={toast?.message}
        action={
          toast?.onUndo ? (
            <Link
              component='button'
              variant='body2'
              color='inherit'
              onClick={toast.onUndo}
              sx={{ fontWeight: 600 }}
            >
              Undo
            </Link>
          ) : undefined
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default CareSearch;
